import { ArrowRightOutlined, DeleteOutlined } from '@ant-design/icons';
import AttributeModel from '@mm/state/model/AttributeModel';
import { ConnectionVariableModel } from '@mm/state/model/ConnectionVariableModel';
import { selectAttributes } from '@mm/state/selectors/attributeSelectors';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { PrimaryButton } from 'src/common/buttons/PrimaryButton/PrimaryButton';
import PrimarySelect, {
  DropdownOption,
} from 'src/common/components/PrimarySelect/PrimarySelect';
import TextInput from 'src/common/components/TextInput';
import { CustomAttributeModel } from '../types';
import styles from './AttributesMappingRow.module.css';

type Props = {
  connectionVariables: ConnectionVariableModel[];
  variable: ConnectionVariableModel;
  outerOptions: DropdownOption[];
  onDelete: () => void;
  onChange: (updatedField: Partial<ConnectionVariableModel>) => void;
  customValues: CustomAttributeModel[];
  setCustomValues: React.Dispatch<React.SetStateAction<CustomAttributeModel[]>>;
};

export const AttributesMappingRow = ({
  connectionVariables,
  variable,
  outerOptions,
  onDelete,
  onChange,
  customValues,
  setCustomValues,
}: Props) => {
  const attributes = useSelector(selectAttributes);
  const availableAttributes = attributes.filter(
    attr => !connectionVariables?.some(v => v.name === attr.name),
  );

  const [selectedAttribute, setSelectedAttribute] = useState<
    AttributeModel | CustomAttributeModel | undefined
  >(
    variable.static
      ? customValues.find(v => v.value === variable.value)
      : attributes.find(a => a.name === variable.name),
  );

  const [selectedOuterAttribute, setSelectedOuterAttribute] = useState<
    DropdownOption | undefined
  >(outerOptions.find(o => o.id === variable.sentAs));

  const onOuterChange = (option: DropdownOption) => {
    setSelectedOuterAttribute(option);
    onChange({ sentAs: String(option.id) });
  };

  const [valueInput, setValueInput] = useState('');

  const onAttributeChange = useCallback(
    (option: AttributeModel | CustomAttributeModel) => {
      setSelectedAttribute(option);
      const attrs = option.value
        ? { value: String(option.value), static: true }
        : { value: null, static: false };

      onChange({ name: option.name, ...attrs });
    },
    [setSelectedAttribute, onChange],
  );

  const addCustomValue = useCallback(
    (value: string) => {
      const existedOption = customValues.find(v => v.id === value);
      if (existedOption) {
        onAttributeChange(existedOption);
      }
      const option: CustomAttributeModel = {
        id: value,
        name: value,
        value,
      };

      setCustomValues(arr => [...arr, option]);
      onAttributeChange(option);
    },
    [customValues, setCustomValues, onAttributeChange],
  );

  const valueInputEl = useMemo(
    () => (
      <div className={styles.customInputContainer}>
        <TextInput
          className=""
          value={valueInput}
          onChange={e => setValueInput(e.target.value)}
          placeholder="Custom Value"
        />
        <PrimaryButton
          className=""
          onClick={() => addCustomValue(valueInput)}
          size="md"
          disabled={valueInput.length === 0}
        >
          Add
        </PrimaryButton>
      </div>
    ),
    [valueInput, addCustomValue],
  );

  return (
    <div className="connection-variable-row">
      <PrimarySelect
        className="connection-variable-row__primary-select"
        placeholder="SalesForce Attribute"
        onChange={onOuterChange}
        options={outerOptions}
        value={selectedOuterAttribute}
        customDropdownStyles={{ zIndex: '10000010' }}
        showSearchFilter
      />
      <ArrowRightOutlined className="connection-variable-row__arrow-icon" />
      <PrimarySelect
        className="connection-variable-row__primary-select"
        placeholder="Customers.ai Attribute"
        onChange={onAttributeChange}
        options={availableAttributes.concat(customValues as any[])}
        value={selectedAttribute}
        customDropdownStyles={{ zIndex: '10000010' }}
        underSearchInsert={valueInputEl}
        showSearchFilter
      />
      <DeleteOutlined
        className="connection-variable-row__trash-icon"
        onClick={onDelete}
      />
    </div>
  );
};
